import { useState } from "react";
import ArabicToggle from "@/components/ArabicToggle";
import { Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { LOCAL_FINAL_SPECS } from "@/data/localFinalSpec";
import { LOCAL_AEO_STRATEGY_SPECS } from "@/data/localAeoStrategySpec";
import { LocalAeoPlanFactsBlock } from "@/components/LocalAeoStrategyBlocks";
import { trackSectionCtaClick } from "@/lib/analytics";
import { ArrowRight, CheckCircle, MapPin, Star, Clock, Globe, Smartphone, BarChart2, Coffee, Dumbbell, UtensilsCrossed, Sparkles } from "lucide-react";

const GEO_AREAS = [
  "Richmond", "Putney", "Wimbledon", "Kingston upon Thames",
  "Twickenham", "Kew", "Barnes", "Sheen", "Mortlake",
  "Guildford", "Epsom", "Esher", "Cobham", "Woking",
  "SW London", "Surrey",
];

const LEGACY_FAQS = [
  {
    q: "How do I get my cafe or gym to appear in 'near me' searches in Richmond, Wimbledon, or Putney?",
    a: "Appearing in 'near me' searches in Richmond, Wimbledon, Putney, and across SW London requires three things working together: a fully optimised Google Business Profile with accurate categories and geo-coordinates, complete LocalBusiness JSON-LD schema on your website, and consistent NAP (name, address, phone) citations across local directories. Our Local Presence plan implements all three within the first 30 days, specifically targeting the SW London and Surrey postcode clusters where your customers are searching.",
  },
  {
    q: "What local search terms do you target for businesses in Kingston, Twickenham, and Guildford?",
    a: "We build keyword clusters around high-intent 'near me' and location-specific queries for your business type. For a cafe in Kingston, that means targeting 'coffee shop Kingston upon Thames', 'cafe near Kingston station', 'best brunch Kingston', and 'cafe open Sunday Kingston'. For a gym in Guildford, we target 'gym near me Guildford', 'personal trainer Guildford', 'fitness studio Guildford town centre', and 'gym with classes Guildford Surrey'. Every cluster is mapped to your Google Business Profile categories and schema markup.",
  },
  {
    q: "Will my restaurant appear in Google AI Overviews and ChatGPT when someone searches for restaurants near Wimbledon?",
    a: "Yes, with the right schema and content strategy. When someone asks ChatGPT or Google's AI Overview 'best Italian restaurant near Wimbledon' or 'restaurants open late Putney', the businesses that appear are those with complete LocalBusiness schema including cuisine type, price range, opening hours, and verified reviews. Our Local Presence plan implements this schema and optimizes your Google Business Profile to maximize your chances of appearing in these AI-generated answers.",
  },
  {
    q: "Do you serve businesses in Surrey towns like Esher, Cobham, and Woking?",
    a: "Yes. We serve independent businesses across the entire SW London and Surrey corridor including Richmond, Putney, Wimbledon, Kingston, Twickenham, Kew, Barnes, Sheen, Mortlake, Guildford, Epsom, Esher, Cobham, and Woking. For businesses in Surrey towns, we implement county-level and district-level schema markup alongside Google Business Profile optimisation to capture both 'near me' mobile searches and specific town-name queries.",
  },
  {
    q: "How long does it take for a pilates studio in SW London to rank in local search?",
    a: "Most clients in Richmond, Putney, and Wimbledon see measurable improvements in Google Maps local pack appearances within 30 to 45 days of schema implementation and GBP optimisation. AI citation in tools like ChatGPT and Perplexity typically follows within 60 to 90 days. Voice search results (Siri, Google Assistant) often update within 2 to 4 weeks once your schema is validated and your GBP is fully optimised.",
  },
  {
    q: "What is included in the weekly AI visibility audit for my local business?",
    a: "Each Monday you receive a one-page PDF covering your Google Business Profile completeness score, local keyword ranking positions for your top 10 target queries in your area, review sentiment summary, schema markup health check, and three prioritised recommendations for the week. We benchmark your visibility against up to three local competitors in your specific SW London or Surrey postcode area.",
  },
  {
    q: "Can you help a gym or fitness studio in Wimbledon get more Google reviews?",
    a: "Yes. Review acquisition is a core part of the Local Presence plan. We provide branded review request templates for email, SMS, and in-studio QR codes, a monthly Google Posts calendar to keep your profile active, and review response templates that incorporate local keywords naturally. Higher review volume and velocity directly improve your local pack ranking and your chances of appearing in AI-generated local recommendations.",
  },
  {
    q: "Is the Local Presence plan available for Arabic-speaking businesses in the MENA region?",
    a: "Yes. All audit reports, recommendation PDFs, and onboarding materials are available in both English and Arabic. We implement hreflang markup and Arabic-language LocalBusiness schema for MENA businesses, ensuring visibility in Arabic-language AI search and Google Maps queries across the UAE, Saudi Arabia, Egypt, and wider MENA markets. Arabic materials are part of the offer described here. Do not link an Arabic URL for this plan unless that exact URL is already HTTP 200. It is not.",
  },
  {
    q: "What is the contract length and can I cancel?",
    a: "The Local Presence plan is billed monthly with no long-term contract. You can cancel with 30 days' notice. A one-time setup fee of £99 covers the initial schema implementation, GBP audit, competitor analysis, and onboarding call. When you are ready to add a full AI website or Google Ads, you can upgrade to the AI Visibility (£599/mo) or Full AI Growth Engine (£1,299/mo) plan at any time.",
  },
];

const INCLUDED = [
  { icon: <BarChart2 className="w-5 h-5" />, title: "Weekly Local Visibility Report", desc: "Monday PDF: local keyword positions, GBP score, schema health and 3 prioritised actions" },
  { icon: <MapPin className="w-5 h-5" />, title: "Google Business Profile Optimisation", desc: "Full audit, category optimisation, photo strategy, Q&A, and monthly Google Posts" },
  { icon: <Globe className="w-5 h-5" />, title: "LocalBusiness Schema Markup", desc: "JSON-LD: name, address, geo-coordinates, hours, price range, menu/services, and reviews where you have genuine reviews shown on the page" },
  { icon: <Star className="w-5 h-5" />, title: "Review Management", desc: "Branded response templates, review acquisition strategy, and QR code request cards" },
  { icon: <Smartphone className="w-5 h-5" />, title: "Google Maps and Citation Consistency", desc: "Embed optimisation, directions markup, and NAP consistency across the main local directories we agree with you" },
  { icon: <Clock className="w-5 h-5" />, title: "1 LinkedIn Post per Week", desc: "Community-focused content to build local social proof and drive foot traffic" },
];

const LOCAL_BUSINESS_CATEGORIES = [
  {
    emoji: "☕",
    label: "Café and Coffee Shop",
    desc: "Clear details for 'coffee near me' searches across Richmond, Putney and Wimbledon.",
    href: "/local/cafe-marketing-richmond-putney-wimbledon/",
    accent: "#FF8C00",
  },
  {
    emoji: "🏋️",
    label: "Gym and Fitness Studio",
    desc: "Clear details for 'gym near me' searches across Putney, Wimbledon and Kingston.",
    href: "/local/gym-marketing-putney-wimbledon-kingston/",
    accent: "#00D4AA",
  },
  {
    emoji: "🧘",
    label: "Pilates Studio",
    desc: "Clear class and booking details for 'pilates near me' searches across SW London and Surrey.",
    href: "/local/pilates-studio-marketing-sw-london-surrey/",
    accent: "#A78BFA",
  },
  {
    emoji: "🍽️",
    label: "Restaurant",
    desc: "Clear menu and booking details for 'restaurants near me' searches in Richmond, Kingston and Guildford.",
    href: "/local/restaurant-marketing-richmond-kingston-guildford/",
    accent: "#FF8C00",
  },
  {
    emoji: "💇",
    label: "Hair Salon and Beauty",
    desc: "Clear treatment and booking details for 'hair salon near me' searches across SW London and Surrey.",
    href: "/local/salon-beauty-marketing-sw-london-surrey/",
    accent: "#EC4899",
  },
  {
    emoji: "🏥",
    label: "Dental practices",
    desc: "Local visibility for dental practices in SW London, written with GDC and ASA guidance in mind.",
    href: "/local/clinic-medical-dental-marketing-sw-london/",
    accent: "#00D4AA",
  },
  {
    emoji: "⚖️",
    label: "Law Firm and Solicitors",
    desc: "AI marketing for law firms and solicitors across SW London and Surrey, with your compliance officer's sign-off.",
    href: "/local/law-firm-solicitor-marketing-sw-london-surrey/",
    accent: "#7C3AED",
  },
  {
    emoji: "🌟",
    label: "Tanning Salon",
    desc: "Clear details for 'tanning salon near me' searches across SW London and Surrey.",
    href: "/local/tanning-salon-marketing-sw-london-surrey/",
    accent: "#F59E0B",
  },
  {
    emoji: "🔧",
    label: "Tyre Services, MOT and Garage",
    desc: "Clear details for 'MOT near me' and 'tyre fitting near me' searches.",
    href: "/local/tyre-mot-garage-marketing-sw-london-surrey/",
    accent: "#6B7280",
  },
];

export default function ServiceLocalPresence() {
  const planSpec = LOCAL_FINAL_SPECS["/services/local-presence-plan/"];
  const strategySpec = LOCAL_AEO_STRATEGY_SPECS["/services/local-presence-plan/"];
  const FAQS = strategySpec?.faqs ?? planSpec?.faqs ?? LEGACY_FAQS;

  const legacySchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/local-presence-plan/",
        "url": "https://integrated.social/services/local-presence-plan/",
        "name": "Local Presence Plan: AI Visibility for Local Businesses in SW London and Surrey | £299/mo",
        "description": "Weekly AI visibility audits, Google Business Profile optimisation, LocalBusiness schema, and 'near me' search targeting for cafes, gyms, pilates studios, and restaurants in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey. From £299/month.",
        "inLanguage": "en-GB",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
            { "@type": "ListItem", "position": 3, "name": "Local Presence Plan", "item": "https://integrated.social/services/local-presence-plan/" },
          ],
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".speakable-summary"],
        },
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/local-presence-plan/#service",
        "name": "Local Presence Plan",
        "alternateName": ["AI Local Visibility for SMEs", "Near Me Search Optimisation SW London", "Local SEO Richmond Putney Wimbledon Kingston Surrey"],
        "description": "Monthly subscription delivering weekly AI visibility audits, Google Business Profile optimisation, LocalBusiness JSON-LD schema, Google Maps integration, review management, and one LinkedIn post per week. Hyper-local targeting for businesses in Richmond, Putney, Wimbledon, Kingston, Twickenham, Guildford, and across SW London and Surrey.",
        "url": "https://integrated.social/services/local-presence-plan/",
        "serviceType": "Local SEO and AI Visibility",
        "areaServed": [
          { "@type": "City", "name": "Richmond", "containedInPlace": { "@type": "AdministrativeArea", "name": "London" } },
          { "@type": "City", "name": "Putney", "containedInPlace": { "@type": "AdministrativeArea", "name": "London" } },
          { "@type": "City", "name": "Wimbledon", "containedInPlace": { "@type": "AdministrativeArea", "name": "London" } },
          { "@type": "City", "name": "Kingston upon Thames", "containedInPlace": { "@type": "AdministrativeArea", "name": "London" } },
          { "@type": "City", "name": "Twickenham", "containedInPlace": { "@type": "AdministrativeArea", "name": "London" } },
          { "@type": "City", "name": "Guildford", "containedInPlace": { "@type": "AdministrativeArea", "name": "Surrey" } },
          { "@type": "AdministrativeArea", "name": "SW London" },
          { "@type": "AdministrativeArea", "name": "Surrey" },
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Place", "name": "EMEA" },
          { "@type": "Place", "name": "MENA" },
        ],
        "audience": {
          "@type": "Audience",
          "audienceType": "Independent local businesses including cafes, gyms, pilates studios, restaurants, salons, clinics, and retailers in SW London and Surrey",
        },
        "offers": {
          "@type": "Offer",
          "price": "299",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": "https://integrated.social/services/local-presence-plan/",
          "description": "Monthly subscription. £99 one-time setup fee. No long-term contract.",
        },
        "provider": { "@id": "https://integrated.social/#organization" },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Local Presence Plan Features",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Weekly Local Visibility Report Report" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Google Business Profile Optimisation" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "LocalBusiness JSON-LD Schema Markup with Geo-Coordinates" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Google Maps Integration and NAP Citation Consistency" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Review Management Templates and Acquisition Strategy" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Weekly LinkedIn Post for Local Community Engagement" } },
          ],
        },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/local-presence-plan/#faq",
        "mainEntity": FAQS.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a,
          },
        })),
      },
    ],
  };

  const schema = strategySpec?.schemaGraph ?? planSpec?.schemaGraph ?? legacySchema;

  return (
    <>
      <SEOHead
        title={strategySpec?.title ?? planSpec?.title ?? "Integrated.Social"}
        description={strategySpec?.description ?? planSpec?.description ?? ""}
        canonical="https://integrated.social/services/local-presence-plan/"
        ogImage={planSpec?.ogImage}
        ogImageAlt={planSpec?.ogImageAlt}
        ogImageType={planSpec?.ogImageType}
        ogImageWidth={planSpec?.ogImageWidth ?? undefined}
        ogImageHeight={planSpec?.ogImageHeight ?? undefined}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, "\\u003c") }}
      />
      <Header />
      <main className="bg-[#0a0f1e] text-white min-h-screen">

        {/* Hero */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <span>/</span>
            <Link href="/services/" className="hover:text-white/70 transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white/70">Local Presence Plan</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-3 py-1 mb-6">
                <MapPin className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-amber-400 text-xs font-semibold tracking-wider uppercase">SW London and Surrey</span>
              </div>
              <h1 className="text-display text-4xl lg:text-5xl text-white leading-tight mb-6 speakable-summary">
                {"Get Found in \"Near Me\" Searches Across Richmond, Wimbledon, Putney, Kingston, and Surrey"}
              </h1>
              <p className="text-white/65 text-lg leading-relaxed mb-4">{FAQS[0]?.a}</p>
              <p className="text-white/55 text-base leading-relaxed mb-8">
                Weekly local visibility reports, Google Business Profile optimisation, LocalBusiness schema markup with geo-coordinates, review management, and one LinkedIn post per week, so the details customers and AI tools check about your business are clear and consistent.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  href="/free-ai-growth-audit/"
                  onClick={() => trackSectionCtaClick({ section_name: "local-presence-hero", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                  className="inline-flex items-center gap-2 bg-amber-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-amber-300 transition-colors"
                >
                  Check my AI visibility<ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/contact/"
                  onClick={() => trackSectionCtaClick({ section_name: "local-presence-hero", cta_text: "Book a call", destination: "/contact/" })}
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-lg hover:border-white/40 transition-colors"
                >
                  Book a Call
                </Link>
              </div>
              <p className="text-white/40 text-sm">£299 a month inc. VAT, plus £99 setup inc. VAT. Monthly billing, cancel with 30 days' notice. Paid ads are not included.</p>
            </div>

            {/* Pricing card */}
            <div className="bg-white/5 border border-amber-400/30 rounded-xl p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-amber-400 text-sm font-semibold tracking-wider uppercase mb-1">Local Presence</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-white">£299</span>
                    <span className="text-white/50">/mo</span>
                  </div>
                  <p className="text-white/40 text-sm mt-1">+ £99 one-time setup</p>
                </div>
                <span className="bg-amber-400/15 text-amber-400 text-xs font-semibold px-3 py-1 rounded-full border border-amber-400/25">SME Starter</span>
              </div>
              <ul className="space-y-3 mb-8">
                {INCLUDED.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">{item.title}</p>
                      <p className="text-white/45 text-xs">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                href="/free-ai-growth-audit/"
                onClick={() => trackSectionCtaClick({ section_name: "local-presence-card", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-amber-300 transition-colors"
              >
                Check my AI visibility<ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-center text-white/35 text-xs mt-3">Available in English and Arabic. UK, EMEA, and MENA.</p>
            </div>
          </div>
        </section>

        {/* Geo areas strip */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <p className="text-white/40 text-xs font-semibold tracking-wider uppercase mb-4">Areas We Serve</p>
          <div className="flex flex-wrap gap-2">
            {GEO_AREAS.map((area) => (
              <span key={area} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/60 text-sm px-3 py-1.5 rounded-full">
                <MapPin className="w-3 h-3 text-amber-400" />
                {area}
              </span>
            ))}
          </div>
        </section>

        {/* Who it's for */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">Built for Independent Local Businesses in SW London and Surrey</h2>
          <p className="text-white/55 mb-8 max-w-2xl">
            If your business depends on foot traffic, local search, or community reputation in Richmond, Putney, Wimbledon, Kingston, Twickenham, Guildford, or the wider SW London and Surrey area, this plan is designed for you.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LOCAL_BUSINESS_CATEGORIES.map((cat) => (
              <Link
                key={cat.label}
                href={cat.href}
                className="group flex flex-col gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-4 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{cat.emoji}</span>
                  <span className="text-white font-semibold text-sm group-hover:text-white/90">{cat.label}</span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed group-hover:text-white/65 transition-colors">{cat.desc}</p>
                <span className="text-xs font-semibold mt-1" style={{ color: cat.accent }}>See industry guide →</span>
              </Link>
            ))}
          </div>
        </section>

        {/* What's included */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">What You Get Every Month</h2>
          <p className="text-white/55 mb-10 max-w-2xl">
            Every deliverable is designed to make your details clear and consistent in local AI search, Google Maps "near me" results and voice queries across SW London and Surrey. We cannot promise any position.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDED.map((item) => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-amber-400/30 transition-colors">
                <div className="text-amber-400 mb-3">{item.icon}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Near me intent section */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Why clear schema helps "near me" searches
              </h2>
              <p className="text-white/65 leading-relaxed mb-4">
                When someone in Wimbledon asks Google "pilates studio near me" or a visitor in Richmond asks ChatGPT "cafes open Sunday morning near Richmond station," the results are no longer just ranked links. They are AI-generated answers drawn from structured data, Google Business Profiles, and verified reviews.
              </p>
              <p className="text-white/65 leading-relaxed mb-4">
                A cafe in Putney with complete LocalBusiness schema, including location, opening hours and price range, gives Google and AI tools clearer facts to work from. We cannot promise any position or appearance.
              </p>
              <p className="text-white/65 leading-relaxed mb-6">
                The Local Presence plan implements the full schema stack for your business type: LocalBusiness with the correct subtype (CafeOrCoffeeShop, HealthClub, Restaurant, BeautySalon), OpeningHoursSpecification for regular and special hours, GeoCoordinates and PriceRange. Review markup is added only where you have genuine customer reviews published on a review platform and shown on the page, and it must match them.
              </p>
              <Link
                href="/services/seo-aeo-geo/"
                onClick={() => trackSectionCtaClick({ section_name: "local-presence-schema", cta_text: "Learn about AEO", destination: "/services/seo-aeo-geo/" })}
                className="inline-flex items-center gap-2 text-amber-400 text-sm font-medium hover:text-amber-300 transition-colors"
              >
                Learn how AEO and GEO work for local businesses
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {[
                { query: "cafe near me Richmond", intent: "Foot traffic, immediate visit", schema: "CafeOrCoffeeShop + OpeningHours + GeoCoordinates" },
                { query: "pilates studio Wimbledon", intent: "Class booking, local loyalty", schema: "HealthClub + hasOfferCatalog + AggregateRating" },
                { query: "gym near me Putney", intent: "Membership, high LTV", schema: "HealthClub + PriceRange + Review" },
                { query: "restaurants open Sunday Kingston", intent: "Immediate booking", schema: "Restaurant + OpeningHoursSpecification + servesCuisine" },
                { query: "hair salon near me Guildford", intent: "Appointment booking", schema: "BeautySalon + makesOffer + telephone" },
              ].map((row) => (
                <div key={row.query} className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <p className="text-amber-400 font-mono text-sm mb-1">"{row.query}"</p>
                  <p className="text-white/55 text-xs mb-1"><span className="text-white/40">Intent:</span> {row.intent}</p>
                  <p className="text-white/55 text-xs"><span className="text-white/40">Schema:</span> {row.schema}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Upgrade path */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Ready to Scale Beyond Local?</h2>
          <p className="text-white/55 mb-8 max-w-2xl">
            The Local Presence plan is your foundation. When you are ready to add a full AI website, Google Ads, or LinkedIn management, upgrade to one of our growth plans.
          </p>
          <div className="overflow-x-auto border border-white/10 rounded-xl mb-8">
            <table className="w-full min-w-[620px] text-sm"><thead className="bg-white/5"><tr><th className="px-4 py-3 text-left">Plan</th><th className="px-4 py-3 text-left">Monthly fee (all prices inc. VAT)</th></tr></thead><tbody className="divide-y divide-white/10 text-white/60"><tr><td className="px-4 py-4 text-amber-400">Local Presence</td><td className="px-4 py-4">£299 a month inc. VAT, plus £99 setup inc. VAT</td></tr><tr><td className="px-4 py-4 text-teal-400">AI Visibility</td><td className="px-4 py-4">£599 a month inc. VAT, plus £149 setup inc. VAT</td></tr><tr><td className="px-4 py-4 text-purple-400">Full AI Growth Engine</td><td className="px-4 py-4">£1,299 a month inc. VAT, plus £299 setup inc. VAT, 3-month minimum</td></tr></tbody></table>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-teal-400/30 transition-colors">
              <p className="text-teal-400 text-xs font-semibold tracking-wider uppercase mb-2">Next Step</p>
              <h3 className="text-white font-bold text-lg mb-2">AI Visibility Plan</h3>
              <p className="text-white/55 text-sm mb-4">Add a 5-page AI website, Google Ads management (ad spend not included), GA4 attribution, and weekly content recommendations. £599 a month inc. VAT.</p>
              <Link
                href="/services/ai-visibility-plan/"
                onClick={() => trackSectionCtaClick({ section_name: "local-presence-upgrade", cta_text: "View AI Visibility Plan", destination: "/services/ai-visibility-plan/" })}
                className="inline-flex items-center gap-2 text-teal-400 text-sm font-medium hover:text-teal-300 transition-colors"
              >
                View AI Visibility Plan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-purple-400/30 transition-colors">
              <p className="text-purple-400 text-xs font-semibold tracking-wider uppercase mb-2">Full Growth</p>
              <h3 className="text-white font-bold text-lg mb-2">Full AI Growth Engine</h3>
              <p className="text-white/55 text-sm mb-4">Full website build, Agentic AI lead gen, LinkedIn management (3 posts/week), and PPC with ad spend. £1,299 a month inc. VAT, 3-month minimum.</p>
              <Link
                href="/services/full-ai-growth-engine/"
                onClick={() => trackSectionCtaClick({ section_name: "local-presence-upgrade", cta_text: "View Full AI Growth Engine", destination: "/services/full-ai-growth-engine/" })}
                className="inline-flex items-center gap-2 text-purple-400 text-sm font-medium hover:text-purple-300 transition-colors"
              >
                View Full AI Growth Engine <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-xl font-bold text-white mb-6">Related Services</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "AI Websites", href: "/services/ai-websites/" },
              { label: "SEO, AEO and GEO", href: "/services/seo-aeo-geo/" },
              { label: "Google AI Mode Optimisation", href: "/services/google-ai-mode-optimisation/" },
              { label: "AI Visibility Audit", href: "/free-ai-growth-audit/" },
              { label: "Preferred Sources Strategy", href: "/services/preferred-sources-strategy/" },
              { label: "AI Content Operations", href: "/services/seo-aeo-geo/" },
              { label: "PPC and Performance Max", href: "/services/ppc-performance-max/" },
            ].map((s) => (
              <Link
                key={s.href}
                href={s.href}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/70 text-sm px-4 py-2 rounded-full hover:border-amber-400/30 hover:text-white transition-colors"
              >
                {s.label} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </section>

        {strategySpec ? <section className="border-t border-white/10 py-12"><div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"><LocalAeoPlanFactsBlock route="/services/local-presence-plan/" /></div></section> : null}

        {/* FAQ */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.q} className="border-b border-white/10 pb-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center border-t border-white/10">
          <h2 className="text-3xl font-bold text-white mb-4">Start with a Free AI Visibility Score</h2>
          <p className="text-white/55 mb-8 max-w-xl mx-auto">
            Enter your business URL for a Free AI Visibility Score: a 0 to 100 readiness score for your website, plus the areas to review first. No card needed.
          </p>
          <Link
            href="/free-ai-growth-audit/"
            onClick={() => trackSectionCtaClick({ section_name: "local-presence-cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
            className="inline-flex items-center gap-2 bg-amber-400 text-black font-semibold px-8 py-4 rounded-lg hover:bg-amber-300 transition-colors text-lg"
          >
            Check my AI visibility<ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-white/35 text-sm mt-4">Serving Richmond, Putney, Wimbledon, Kingston, Guildford, and all of SW London and Surrey.</p>
        </section>

      </main>
      <Footer />
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
